import { databases, databaseId, appwriteConfig } from '../config/appwrite';
import { SocialMediaBackground } from '../types';
import { ID, Query } from 'appwrite';
import { StorageService } from './storageService';

export class SocialMediaBackgroundService {
  private static collectionId = appwriteConfig.collections.socialMediaBackgrounds;

  // Alle Hintergründe eines bestimmten Typs abrufen
  static async getBackgroundsByType(type: 'profile' | 'banner' | 'sharepic'): Promise<SocialMediaBackground[]> {
    try {
      console.log(`Lade ${type}-Hintergründe von Collection: ${this.collectionId}`);
      
      const response = await databases.listDocuments(
        databaseId,
        this.collectionId,
        [
          Query.equal('type', type),
          Query.orderDesc('$createdAt')
        ]
      );

      console.log(`Gefundene ${type}-Hintergründe:`, response.documents.length);

      return response.documents.map((doc: any) => ({
        id: doc.$id,
        name: doc.name,
        description: doc.description || '',
        type: doc.type,
        storageUrl: StorageService.getFileViewUrlFromStorageUrl(doc.storageUrl),
        storageFileId: doc.storageFileId,
        isActive: doc.isActive,
        uploadedBy: doc.uploadedBy,
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      console.error(`Fehler beim Laden der ${type}-Hintergründe:`, error);
      console.error('Collection ID:', this.collectionId);
      console.error('Database ID:', databaseId);
      
      if (error instanceof Error) {
        if (error.message.includes('Collection not found')) {
          throw new Error('Die Bildvorlagen sind derzeit nicht verfügbar. Bitte kontaktiere die Administration.');
        } else if (error.message.includes('Database not found')) {
          throw new Error('Die Bildvorlagen sind derzeit nicht verfügbar. Bitte kontaktiere die Administration.');
        } else {
          throw new Error(`${type}-Hintergründe konnten nicht geladen werden: ${error.message}`);
        }
      }
      throw new Error(`${type}-Hintergründe konnten nicht geladen werden`);
    }
  }

  // Aktiven Hintergrund eines Typs abrufen
  static async getActiveBackground(type: 'profile' | 'banner' | 'sharepic'): Promise<SocialMediaBackground | null> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        this.collectionId,
        [
          Query.equal('type', type),
          Query.equal('isActive', true),
          Query.limit(1)
        ]
      );

      if (response.documents.length === 0) {
        return null;
      }

      const doc = response.documents[0];
      return {
        id: doc.$id,
        name: doc.name,
        description: doc.description || '',
        type: doc.type,
        storageUrl: StorageService.getFileViewUrlFromStorageUrl(doc.storageUrl),
        storageFileId: doc.storageFileId,
        isActive: doc.isActive,
        uploadedBy: doc.uploadedBy,
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      console.error('Fehler beim Laden des aktiven Hintergrunds:', error);
      throw new Error('Aktiver Hintergrund konnte nicht geladen werden');
    }
  }

  // Neuen Hintergrund erstellen
  static async createBackground(
    name: string,
    type: 'profile' | 'banner' | 'sharepic',
    file: File,
    uploadedBy: string,
    description?: string
  ): Promise<SocialMediaBackground> {
    try {
      // Datei in Storage hochladen
      const storageUrl = await StorageService.uploadProfileBackground(file);
      
      // File-ID aus der URL extrahieren
      const urlParts = storageUrl.split('/');
      const storageFileId = urlParts[urlParts.length - 2]; // File-ID ist vor dem letzten Segment

      // Alle anderen Hintergründe des gleichen Typs deaktivieren
      await this.deactivateAllBackgroundsOfType(type);

      // Neuen Hintergrund in Datenbank speichern
      const doc = await databases.createDocument(
        databaseId,
        this.collectionId,
        ID.unique(),
        {
          name,
          description: description || '',
          type,
          storageUrl,
          storageFileId,
          isActive: true,
          uploadedBy,
        }
      );

      return {
        id: doc.$id,
        name: doc.name,
        description: doc.description || '',
        type: doc.type,
        storageUrl: StorageService.getFileViewUrlFromStorageUrl(doc.storageUrl),
        storageFileId: doc.storageFileId,
        isActive: doc.isActive,
        uploadedBy: doc.uploadedBy,
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      console.error('Fehler beim Erstellen des Hintergrunds:', error);
      console.error('Collection ID:', this.collectionId);
      console.error('Database ID:', databaseId);
      
      if (error instanceof Error) {
        if (error.message.includes('Collection not found')) {
          throw new Error('Die Bildvorlagen sind derzeit nicht verfügbar. Bitte kontaktiere die Administration.');
        } else if (error.message.includes('Database not found')) {
          throw new Error('Die Bildvorlagen sind derzeit nicht verfügbar. Bitte kontaktiere die Administration.');
        } else {
          throw new Error(`Hintergrund konnte nicht erstellt werden: ${error.message}`);
        }
      }
      throw new Error('Hintergrund konnte nicht erstellt werden');
    }
  }

  // Hintergrund aktivieren (deaktiviert alle anderen des gleichen Typs)
  static async activateBackground(backgroundId: string): Promise<void> {
    try {
      // Aktuellen Hintergrund abrufen
      const background = await databases.getDocument(
        databaseId,
        this.collectionId,
        backgroundId
      );

      // Alle anderen Hintergründe des gleichen Typs deaktivieren
      await this.deactivateAllBackgroundsOfType(background.type);

      // Diesen Hintergrund aktivieren
      await databases.updateDocument(
        databaseId,
        this.collectionId,
        backgroundId,
        {
          isActive: true,
        }
      );
    } catch (error) {
      console.error('Fehler beim Aktivieren des Hintergrunds:', error);
      throw new Error('Hintergrund konnte nicht aktiviert werden');
    }
  }

  // Hintergrund löschen
  static async deleteBackground(backgroundId: string): Promise<void> {
    try {
      // Hintergrund aus Datenbank abrufen
      const background = await databases.getDocument(
        databaseId,
        this.collectionId,
        backgroundId
      );

      // Aus Storage löschen
      await StorageService.deleteImage(background.storageFileId);

      // Aus Datenbank löschen
      await databases.deleteDocument(
        databaseId,
        this.collectionId,
        backgroundId
      );
    } catch (error) {
      console.error('Fehler beim Löschen des Hintergrunds:', error);
      throw new Error('Hintergrund konnte nicht gelöscht werden');
    }
  }

  // Alle Hintergründe eines Typs deaktivieren
  private static async deactivateAllBackgroundsOfType(type: 'profile' | 'banner' | 'sharepic'): Promise<void> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        this.collectionId,
        [
          Query.equal('type', type),
          Query.equal('isActive', true)
        ]
      );

      // Alle aktiven Hintergründe deaktivieren
      for (const doc of response.documents) {
        await databases.updateDocument(
          databaseId,
          this.collectionId,
          doc.$id,
          {
            isActive: false,
          }
        );
      }
    } catch (error) {
      console.error('Fehler beim Deaktivieren der Hintergründe:', error);
      throw new Error('Hintergründe konnten nicht deaktiviert werden');
    }
  }
}
